import React from "react";
import { Timer, BarChart3, PlusCircle, ArrowRightCircle } from "lucide-react";
import {
  CREW_HOURS_BULLETS,
  ANNUAL_ALLOWANCE_BULLETS,
  ADDON_EXAMPLES,
  ADDON_PRICING_NOTES,
  CONTRACTOR_SERVICE_TRIGGERS,
} from "@/data/maintenancePlans";

function PanelHead({ Icon, eyebrow, title }) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <Icon size={26} className="text-br-bronze shrink-0 mt-1" />
      <div>
        <div className="br-eyebrow text-br-bronze">{eyebrow}</div>
        <h2 className="br-headline text-2xl sm:text-3xl mt-1">{title}</h2>
      </div>
    </div>
  );
}

function Bullets({ items, testId }) {
  return (
    <ul className="mt-3 space-y-1.5 text-sm text-gray-700" data-testid={testId}>
      {items.map((b) => (
        <li key={b} className="flex items-start gap-2">
          <span className="text-br-bronze mt-1 leading-none">•</span>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

export default function CrewHoursAndAllowanceSection() {
  return (
    <section className="section-pad bg-white" id="crew-hours-and-allowance" data-testid="crew-hours-and-allowance">
      <div className="container-br space-y-6">
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-br-paper border border-gray-200 rounded p-6" data-testid="crew-hours-section">
            <PanelHead Icon={Timer} eyebrow="Member Labor" title="How Crew Hours Work" />
            <p className="text-sm text-gray-700 leading-relaxed">
              Each membership includes defined crew-hour limits. Crew hours are the labor time that may be applied toward approved covered maintenance items during a visit or covered request.
            </p>
            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
              Crew hours help keep the membership clear and fair. They define how much labor can be applied to a covered request without turning the plan into unlimited repair work.
            </p>
            <Bullets items={CREW_HOURS_BULLETS} testId="crew-hours-bullets" />
          </div>

          <div className="bg-br-paper border border-gray-200 rounded p-6" data-testid="allowance-section">
            <PanelHead Icon={BarChart3} eyebrow="Annual Reserve" title="What Is the Annual Covered-Work Allowance?" />
            <p className="text-sm text-gray-700 leading-relaxed">
              The annual covered-work allowance is the maximum annual reserve value that may be applied toward approved covered work within the membership rules. It is not cash value, does not guarantee all repairs are covered, and does not create unlimited labor.
            </p>
            <Bullets items={ANNUAL_ALLOWANCE_BULLETS} testId="allowance-bullets" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-br-paper border border-gray-200 rounded p-6" data-testid="addon-section">
            <PanelHead Icon={PlusCircle} eyebrow="Add-On Work" title="When We Find Something That Needs Extra Work" />
            <p className="text-sm text-gray-700 leading-relaxed">
              If Black Ridge finds something during a visit that is outside the included maintenance scope, we may recommend an add-on or contractor-service estimate.
            </p>
            <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-700">
              {ADDON_EXAMPLES.map((e) => (
                <div key={e} className="flex items-start gap-2">
                  <span className="text-br-bronze mt-1 leading-none">•</span>
                  <span>{e}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-gray-200 pt-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze font-bold mb-2">Add-On Pricing</div>
              <Bullets items={ADDON_PRICING_NOTES} testId="addon-pricing-notes" />
            </div>
          </div>

          <div className="bg-br-paper border border-gray-200 rounded p-6" data-testid="contractor-service-section">
            <PanelHead Icon={ArrowRightCircle} eyebrow="Outside The Lane" title="When a Separate Estimate Is Needed" />
            <p className="text-sm text-gray-700 leading-relaxed">
              Some issues are outside the maintenance lane and require a formal contractor-service estimate. This protects the homeowner and makes sure larger work is scoped, priced, and scheduled properly.
            </p>
            <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-700">
              {CONTRACTOR_SERVICE_TRIGGERS.map((t) => (
                <div key={t} className="flex items-start gap-2">
                  <span className="text-br-bronze mt-1 leading-none">•</span>
                  <span>{t}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-gray-700 italic bg-br-bronze/10 border-l-2 border-br-bronze p-3 rounded">
              “This is outside the maintenance lane, but Black Ridge can route it to the proper contractor-service estimate.”
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
